import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { Icon } from 'js/components';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import {
  findQuestionById,
  findQuestionByKeys,
  listEligibleBreakoutQuestions,
} from 'js/utils/assessment-cohorts';
import { getQuestionMeta } from 'js/utils/questions';
import BreakoutQuestionPickerModal from './BreakoutQuestionPickerModal';
import BreakoutGrouping from './Grouping';
import { compactGroups, GROUPING_MODE } from './Grouping/grouping-utils';
import { isBreakoutNameUnique, isReservedBreakoutName } from './breakout-utils';
import styles from './Breakout.module.scss';

function BreakoutForm({
  assessment,
  categoryColorMap,
  isNew,
  listPath,
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  setFieldTouched,
  isSubmitting,
  dirty,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const selectedQuestion = useMemo(
    () =>
      findQuestionByKeys(
        assessment,
        values.question_set_key,
        values.question_key
      ) || findQuestionById(assessment, values.question_id),
    [
      assessment,
      values.question_set_key,
      values.question_key,
      values.question_id,
    ]
  );

  const eligibleCount = useMemo(
    () => listEligibleBreakoutQuestions(assessment).length,
    [assessment]
  );

  const formikAttributes = (fieldName) => ({
    id: fieldName,
    name: fieldName,
    value: values[fieldName],
    onChange: handleChange,
    onBlur: handleBlur,
    invalid: errors[fieldName] && touched[fieldName],
  });

  const formikFeedback = (fieldName) => {
    return errors[fieldName] && touched[fieldName] ? (
      <FormFeedback>{errors[fieldName]}</FormFeedback>
    ) : null;
  };

  const handleSelectQuestion = (question) => {
    setFieldValue('question_id', question.id);
    setFieldValue(
      'question_set_key',
      question.question_set_key ?? question.questionSet?.key ?? ''
    );
    setFieldValue('question_key', question.key ?? '');
    setFieldValue('grouping_mode', GROUPING_MODE.EACH_ANSWER);
    setFieldValue('groups', {});
    setFieldTouched('question_set_key', true, false);
    setPickerOpen(false);
  };

  const typeMeta = getQuestionMeta(selectedQuestion?.type);
  const answers = (selectedQuestion?.answers || []).filter((a) => !a.is_na);
  const questionInvalid =
    (errors.question_set_key || errors.question_key || errors.question_id) &&
    (touched.question_set_key || touched.question_key || touched.question_id);

  return (
    <Form onSubmit={handleSubmit}>
      <Card tag="section">
        <CardHeader>
          <CardTitle tag="h5">
            <Link to={listPath} className={styles.backLink}>
              <Icon icon={faArrowLeft} className="mr-2" />
              Breakouts
            </Link>
            <span className={styles.titleSep}>/</span>
            {isNew ? 'New breakout' : values.name || 'Edit breakout'}
          </CardTitle>
          <p>
            Choose a name and the question used to carve participants into
            cohorts.
          </p>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label for="name">Name</Label>
            <Input
              type="text"
              className="limited-width-lg"
              {...formikAttributes('name')}
              disabled={isSubmitting}
            />
            {formikFeedback('name')}
          </FormGroup>

          <FormGroup>
            <Label className={styles.questionLabel}>
              Question
              {selectedQuestion ? (
                <Button
                  type="button"
                  color="link"
                  className={styles.changeQuestion}
                  onClick={() => setPickerOpen(true)}
                  disabled={eligibleCount === 0 || isSubmitting}
                >
                  (Change question)
                </Button>
              ) : null}
            </Label>
            {selectedQuestion ? (
              <div className={styles.selectedQuestion}>
                <div className={styles.selectedMeta}>
                  {selectedQuestion.questionSet?.name ||
                    selectedQuestion.questionSet?.key}{' '}
                  / {selectedQuestion.key}
                  {typeMeta.name ? (
                    <>
                      {' · '}
                      <Icon
                        icon={typeMeta.icon}
                        className={styles.typeIcon}
                      />{' '}
                      {typeMeta.name}
                    </>
                  ) : null}
                </div>
                <div className={styles.selectedText}>
                  {selectedQuestion.text}
                </div>
                <BreakoutGrouping
                  answers={answers}
                  groupingMode={values.grouping_mode}
                  groups={values.groups}
                  onGroupingModeChange={(mode) => {
                    setFieldValue('grouping_mode', mode);
                    if (mode !== GROUPING_MODE.CUSTOM) {
                      setFieldValue('groups', {});
                    }
                  }}
                  onGroupsChange={(next) => setFieldValue('groups', next)}
                  disabled={isSubmitting}
                />
              </div>
            ) : (
              <div>
                <Button
                  type="button"
                  color="secondary"
                  onClick={() => setPickerOpen(true)}
                  disabled={eligibleCount === 0 || isSubmitting}
                >
                  Choose question…
                </Button>
                {eligibleCount === 0 ? (
                  <p className="text-muted small mt-2 mb-0">
                    This assessment has no eligible questions for breakouts.
                  </p>
                ) : null}
              </div>
            )}
            {questionInvalid ? (
              <FormFeedback className="d-block">
                {errors.question_set_key ||
                  errors.question_key ||
                  errors.question_id}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </CardBody>
      </Card>

      <div className={styles.saveBar}>
        <Button color="primary" type="submit" disabled={isSubmitting || !dirty}>
          {isSubmitting ? 'Saving…' : 'Save changes'}
        </Button>
        <Link to={listPath} className="btn btn-secondary">
          Cancel
        </Link>
      </div>

      <BreakoutQuestionPickerModal
        isOpen={pickerOpen}
        assessment={assessment}
        categoryColorMap={categoryColorMap}
        selectedQuestionId={values.question_id}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelectQuestion}
      />
    </Form>
  );
}

const FormikBreakoutForm = withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ breakout }) => ({
    name: breakout?.name || '',
    question_id: breakout?.question_ids?.[0] ?? null,
    question_set_key: breakout?.question_set_key || '',
    question_key: breakout?.question_key || '',
    grouping_mode: breakout?.grouping_mode || GROUPING_MODE.EACH_ANSWER,
    groups: breakout?.groups || {},
  }),
  validationSchema: ({ breakouts = [], excludeIndex = null }) =>
    Yup.object().shape({
      name: Yup.string()
        .trim()
        .required('Name is required')
        .test(
          'not-reserved',
          'That name is reserved',
          (value) => !isReservedBreakoutName(value)
        )
        .test(
          'unique',
          'A breakout with this name already exists',
          (value) =>
            !value || isBreakoutNameUnique(breakouts, value, { excludeIndex })
        ),
      question_set_key: Yup.string().trim().required('Choose a question'),
      question_key: Yup.string().trim().required('Choose a question'),
      grouping_mode: Yup.string()
        .oneOf([GROUPING_MODE.EACH_ANSWER, GROUPING_MODE.CUSTOM])
        .required(),
    }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    const { onSubmit = () => {} } = props;
    setSubmitting(true);
    try {
      const groupingMode = values.grouping_mode || GROUPING_MODE.EACH_ANSWER;
      await onSubmit({
        name: values.name.trim(),
        question_id: values.question_id,
        question_set_key: values.question_set_key,
        question_key: values.question_key,
        grouping_mode: groupingMode,
        groups:
          groupingMode === GROUPING_MODE.CUSTOM
            ? compactGroups(values.groups)
            : {},
      });
    } finally {
      setSubmitting(false);
    }
  },
  displayName: 'BreakoutForm',
})(BreakoutForm);

export default FormikBreakoutForm;
